import {useCallback, useEffect, useState} from 'react';
import type {ServiceStatus} from '../api/types';
import {useI18n} from '../i18n';
import {useApi, useGuestSession} from './GuestSessionProvider';

/**
 * Is the venue serving? The read Q20 exists for, and the one Q1 needs before it
 * can decide between a menu and a notice.
 *
 * `verdict` is `null` when the server declined the question — the contract mock
 * answers 501, and the operation's `security` block does not list the guest
 * credential even though its description says guest phones may read it (flagged
 * in `api/client.ts`). **Unknown is not closed.** A guest shown a closed notice
 * in an open restaurant walks out, so a null verdict means carry on to the menu.
 *
 * Never composed on the client. A phone in a dining room does not have the
 * merchant's timezone, a correct clock, or the trading-day cutover rule.
 */
export interface ServiceStatusState {
  verdict: ServiceStatus | null;
  loading: boolean;
  /** True only when the server said `closed` or `outside_hours`. */
  notServing: boolean;
  /**
   * Has the question been *asked and answered* — as opposed to declined, which
   * is an answer, or still in flight, which is not.
   *
   * `verdict === null` cannot carry this: null is both "we have not read yet"
   * and "the server refused to say", and those must not look alike to a caller
   * deciding whether to move a guest. Q1 forwards a scan straight to the menu
   * and may only do so once this is true; without it, a **closed** venue would
   * paint the menu for one frame and then bounce to Q20, which is the flash the
   * `ready` flag on that screen already exists to avoid, promoted from a button
   * to a whole navigation.
   */
  settled: boolean;
  reload: () => void;
}

/**
 * The read itself. Private — the provider owns the only call.
 *
 * ## Why this stopped being a plain per-screen hook
 *
 * This response carries two different things, and only one of them is Q20's.
 * `state` is a question three screens ask; **`time_zone` is a question every
 * screen with a timestamp on it depends on** — and until 2026-08-14 only the
 * three screens that wanted the verdict ever mounted the hook. So a guest who
 * deep-linked to `/status` or `/order/{id}` — a reload at the table, a link from
 * a phone that never passed Q1, exactly how a receipt is reached — had no zone
 * at all, `formatTime` correctly returned `''` rather than falling back to the
 * device's, and the screens rendered their labels around nothing: "สั่งเมื่อ"
 * with no time after it.
 *
 * Mounting the hook on those screens too would have fixed the labels and made
 * the request twice on the screens that already had it, on the one surface whose
 * network we do not own. So the read moved up: **one call per session, at the
 * root, published to everybody.** The hook keeps its name and its shape, so no
 * screen changed.
 */
function useServiceStatusRead(): ServiceStatusState {
  const client = useApi();
  const {setTimeZone} = useI18n();
  const {status: sessionStatus} = useGuestSession();
  const [verdict, setVerdict] = useState<ServiceStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [settled, setSettled] = useState(false);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (sessionStatus !== 'active') return;
    const controller = new AbortController();
    setLoading(true);
    client
      .getServiceStatus(controller.signal)
      .then((next) => {
        setVerdict(next);
        // The venue's clock, for every time this surface renders (C-4, D-023).
        // This read is the only place the zone arrives, so it is the only place
        // that can publish it — and publishing it here rather than at each call
        // site means a screen cannot forget and silently fall back to the
        // device's zone, which is what the pinned `Asia/Bangkok` constant used
        // to hide.
        if (next?.time_zone) setTimeZone(next.time_zone);
      })
      // A failed read is "unknown", not "closed". See above.
      .catch(() => setVerdict(null))
      .finally(() => {
        // `settled` is deliberately NOT reset when `reload` starts a second
        // read: it means "we have had an answer at some point", and a guest
        // already past Q1 must not be pulled backwards because Q20 is retrying.
        // The abort guard is on `loading` only, for the same reason — an
        // unmounted screen's in-flight read still answered the question.
        if (!controller.signal.aborted) setLoading(false);
        setSettled(true);
      });
    return () => controller.abort();
  }, [client, sessionStatus, nonce, setTimeZone]);

  return {
    verdict,
    loading,
    notServing: verdict?.state === 'closed' || verdict?.state === 'outside_hours',
    settled,
    reload,
  };
}

export {useServiceStatusRead};
